import { T, useT } from '@transifex/react';
import { VStack } from '@vidahealth/vida-ui';
import { useState } from 'react';

import { coachDisplayName } from '@/components/CoachSummary';
import { trackEvent } from '@/modules/analytics';
import { useRulesEngine } from '@/modules/rulesEngine';
import { CtaButton, LoadingScreen, Screen, ScreenHeading } from '@/modules/ui';

import { AddToCalendarSheet } from './components/AddToCalendarSheet';
import { SessionCard } from './components/SessionCard';
import { openCalendarEvent, type CalendarProvider } from './utils/calendarLinks';

const dayFmt = new Intl.DateTimeFormat(undefined, {
  weekday: 'long',
  month: 'long',
  day: 'numeric',
  year: 'numeric',
});

const timeFmt = new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit' });

/**
 * COACH MATCHING SUCCESS — confirms the booked initial consult (Sparta's
 * ProviderMatchingSuccessContainer). The primary coach is already assigned (on
 * the availability screen). "Continue" advances to the intro-message picker;
 * "Add To Calendar" opens a provider chooser that builds a real calendar link /
 * .ics for the session.
 */
export function CoachMatchingSuccessScreen() {
  const t = useT();
  const { user, matchedCoaches, selectedCoachUuid, scheduledConsult, setCurrentRoute } =
    useRulesEngine();

  // "Add To Calendar" + the quick-intro step are Split-gated in Sparta; onboarding
  // has no Split, so both are enabled by default here (see featureFlags note).
  const [calendarOpen, setCalendarOpen] = useState(false);

  const coach =
    matchedCoaches?.coaches.find((c) => c.uuid === selectedCoachUuid) ?? matchedCoaches?.coaches[0];

  if (!coach || !scheduledConsult) return <LoadingScreen />;

  const coachName = coachDisplayName(coach);
  const start = new Date(scheduledConsult.date);
  const dayLabel = dayFmt.format(start);
  // Show the range only when the consult end is known; otherwise just the start.
  const timeLabel = scheduledConsult.endDate
    ? `${timeFmt.format(start)} - ${timeFmt.format(new Date(scheduledConsult.endDate))}`
    : timeFmt.format(start);
  const onAddToCalendar = (provider: CalendarProvider) => {
    setCalendarOpen(false);
    trackEvent('coach-matching-success-calendar-add', {
      uuid: user?.uuid,
      calendarType: provider,
      // Sparta sends the coach's USER uuid for coach events, not the profile uuid.
      coachUuid: coach.userUuid,
    });
    // The calendar event needs an end; fall back to a 30-min block when the
    // consult duration is unknown (display still shows only the start time).
    const endIso =
      scheduledConsult.endDate ?? new Date(start.getTime() + 30 * 60_000).toISOString();
    openCalendarEvent(provider, {
      title: t('First session with {name}', { name: coachName }),
      details: t('Video call in the Vida app'),
      startIso: scheduledConsult.date,
      endIso,
      // Unique per user + booking so two users adding the same consult don't
      // generate a colliding UID (RFC 5545 treats identical UIDs as one event).
      uid: `${user?.uuid ?? 'anon'}-${coach.uuid}-${scheduledConsult.date}`,
    });
  };

  return (
    <Screen
      footer={
        <VStack space='md'>
          <CtaButton
            onPress={() => {
              trackEvent('coach-matching-success-click-continue', {
                uuid: user?.uuid,
                coachUuid: coach.userUuid,
                hasScheduledConsult: Boolean(scheduledConsult),
              });
              // On to the quick-intro step (Split-gated in Sparta; on by default here).
              setCurrentRoute('/step/quick-intro-messages');
            }}
            testID='coach-matching-success-continue'
          >
            <T _str='Continue' />
          </CtaButton>
          <CtaButton
            type='secondary'
            onPress={() => setCalendarOpen(true)}
            testID='coach-matching-success-add-to-calendar'
          >
            <T _str='Add To Calendar' />
          </CtaButton>
        </VStack>
      }
    >
      <VStack space='2xl'>
        <ScreenHeading
          title={t('Congrats! Your session is scheduled')}
          subtitle={t(
            'You and {name} will create a care plan personalized to you and discuss your challenges.',
            { name: coachName }
          )}
        />
        <SessionCard coach={coach} dayLabel={dayLabel} timeLabel={timeLabel} />
      </VStack>

      <AddToCalendarSheet
        isOpen={calendarOpen}
        onClose={() => setCalendarOpen(false)}
        onSelect={onAddToCalendar}
      />
    </Screen>
  );
}
